// Pure playback timeline: flattens a song (melody) plus the voiced chords into
// time-ordered events measured in absolute beats of the OPENING meter (a
// quarter in 4/4, an eighth in 6/8 — the unit the tempo counts, as in the
// band) from the start, so a transport can schedule audio and a playhead can
// map time to an x position. Audio-free and fully unit-testable. See task #13.

import type { Bar, Song } from '@/music/song';
import { barVoices } from '@/music/song';
import {
  mergeTiedExpressionEnvelope,
  scaleExpressionEnvelope,
  type PlaybackExpressionPoint,
  type ScoreEventExpression,
} from '@/music/score/expression';

export type TimelineSound = 'lead' | 'piano' | 'bass' | 'guitar';

export interface PlayEvent {
  kind: 'melody' | 'chord';
  /** Authored part identity, independent of the lead/bass playback preferences. */
  sound?: TimelineSound;
  /** Onset in timeline beats (the opening meter's beat) from the start (0-based). */
  absBeat: number;
  durBeats: number;
  midis: number[]; // one for melody, several for a chord
  velocity?: number;
  articulationGate?: number;
  expression?: PlaybackExpressionPoint[];
}

export interface Timeline {
  events: PlayEvent[];
  /** Absolute start beat of each bar, indexed by global bar index. */
  barStarts: number[];
  /** Timeline beats per written beat of each bar: 1 unless the bar's meter
   *  counts a different note value than the opening one (0.5 for a 6/8 bar
   *  after 4/4 — an eighth keeps its written value across the change).
   *  Absent = 1 for every bar. */
  barScales?: number[];
  totalBeats: number;
}

export interface VoicedChordInput {
  barIndex: number;
  beat: number;
  midis: number[];
}

/** Build the playback timeline for a song and its voiced chords.
 *  `extraStaves` (e.g. the grand staff's bass-clef song, which shares the
 *  same bar grid) contribute their melody voices to the same timeline. */
export function buildTimeline(
  song: Song,
  voiced: VoicedChordInput[] = [],
  extraStaves: Song[] = [],
  opts: {
    /** Semitones added to every melody-voice pitch at PLAYBACK. A transposing
     *  part's Song carries WRITTEN pitches; passing the negated transposition
     *  makes it SOUND at concert pitch (clarinet in A: written Eb4 − 3 = C4).
     *  Voiced guitar chords are already concert and are not shifted. */
    melodySemitones?: number;
    /** Full score: per-extraStaff playback shifts (parallel to extraStaves),
     *  so each transposing part sounds at concert. An entry defaults to
     *  melodySemitones (the grand staff's staves share one part). */
    extraSemitones?: number[];
    /** Timbre of the primary authored part and each extra staff. Untagged
     * melody uses the chosen lead voice; grand-staff extras inherit it. */
    melodySound?: TimelineSound;
    extraSounds?: TimelineSound[];
    expressionByEvent?: ReadonlyMap<string, ScoreEventExpression>;
    /** Song beats count each bar's OWN denominator (beats per quarter:
     * 2 in 6/8). Defaults to each bar's meter; expression is in quarters. */
    beatsPerQuarterByBar?: readonly number[];
  } = {},
): Timeline {
  const melodyShift = opts.melodySemitones ?? 0;
  const bars = song.sections.flatMap((s) => s.bars);
  // ONE unit for the whole piece: the opening bar's beat. Summing each bar's
  // own beats made a 6/8 eighth after 4/4 last as long as a quarter.
  const beatsPerQuarter = (bi: number) =>
    opts.beatsPerQuarterByBar?.[bi] ?? (bars[bi]?.timeSig ?? song.timeSignature)[1] / 4;
  const unitPerQuarter = beatsPerQuarter(0);
  const barScales = bars.map((_, bi) => unitPerQuarter / beatsPerQuarter(bi));
  const barStarts: number[] = [];
  let cursor = 0;
  bars.forEach((bar, bi) => {
    barStarts.push(cursor);
    cursor += (bar.beats || 4) * barScales[bi];
  });
  const totalBeats = cursor;

  const voicedAt = new Map(voiced.map((v) => [`${v.barIndex}:${v.beat}`, v.midis]));
  const events: PlayEvent[] = [];
  // TIES MERGE at playback: a tie's continuation extends the started note's
  // event instead of re-striking (a tied whole note used to attack again on
  // the far side of the tie — user-heard in Donna Lee). Keyed per
  // source-staff + voice so independent voices never merge; the continuation
  // must be the SAME pitch starting exactly where the started note ends.
  const openTies = new Map<
    string,
    { ev: PlayEvent; endBeat: number; tie: 'start' | 'stop' | 'both' | undefined; midi: number; dynamicGain: number }
  >();

  bars.forEach((bar, bi) => {
    const start = barStarts[bi];
    const scale = barScales[bi];
    const beats = bar.beats || 4;
    const chords = [...bar.chords].sort((a, b) => a.beat - b.beat);
    chords.forEach((c, i) => {
      const midis = voicedAt.get(`${bi}:${c.beat}`);
      if (!midis || midis.length === 0) return;
      const nextBeat = i + 1 < chords.length ? chords[i + 1].beat : beats + 1;
      events.push({
        kind: 'chord',
        absBeat: start + (c.beat - 1) * scale,
        durBeats: (c.duration || nextBeat - c.beat) * scale,
        midis,
      });
    });
    // Every voice sounds (voices 3+ included), across every staff — each
    // staff with its own concert-pitch compensation.
    const sources = [
      { b: bar as Bar | undefined, shift: melodyShift, sound: opts.melodySound },
      ...extraStaves.map((s, k) => ({
        b: s.sections.flatMap((sec) => sec.bars)[bi] as Bar | undefined,
        shift: opts.extraSemitones?.[k] ?? melodyShift,
        sound: opts.extraSounds?.[k] ?? opts.melodySound,
      })),
    ];
    sources.forEach(({ b, shift, sound }, si) => {
      if (!b) return;
      barVoices(b).forEach((voice, vi) => {
        for (const note of voice) {
          if (note.rest || note.slash || note.grace) continue; // graces are ornament-only for now
          const key = `${si}:${vi}`;
          const abs = start + (note.beat - 1) * scale;
          const duration = note.duration * scale;
          const marks = opts.expressionByEvent?.get(note.id);
          const dynamicGain = marks?.dynamicGain ?? 1;
          const expression = scaleExpressionEnvelope(marks?.dynamicEnvelope, unitPerQuarter);
          const open = openTies.get(key);
          if (
            open &&
            (open.tie === 'start' || open.tie === 'both') &&
            open.midi === note.midi &&
            Math.abs(open.endBeat - abs) < 1e-3
          ) {
            const merged = mergeTiedExpressionEnvelope(
              open.ev.expression, expression, open.ev.durBeats, dynamicGain / open.dynamicGain,
            );
            if (merged) open.ev.expression = merged;
            open.ev.durBeats += duration;
            open.endBeat = abs + duration;
            open.tie = note.tie;
            continue;
          }
          const ev: PlayEvent = {
            kind: 'melody',
            ...(sound ? { sound } : {}),
            absBeat: abs,
            durBeats: duration,
            ...(marks?.velocity !== undefined ? { velocity: marks.velocity } : {}),
            ...(marks?.articulationGate !== undefined ? { articulationGate: marks.articulationGate } : {}),
            ...(expression ? { expression } : {}),
            // A chord event sounds every notehead, not just the top of the stack.
            midis: [note.midi, ...(note.chordNotes?.map((c) => c.midi) ?? [])].map(
              (m) => m + shift,
            ),
          };
          events.push(ev);
          openTies.set(key, {
            ev,
            endBeat: abs + duration,
            tie: note.tie,
            midi: note.midi,
            dynamicGain,
          });
        }
      });
    });
  });

  events.sort((a, b) => a.absBeat - b.absBeat || (a.kind === b.kind ? 0 : a.kind === 'chord' ? -1 : 1));
  return { events, barStarts, barScales, totalBeats };
}

/**
 * A timeline position on the DRAWN bar grid, where each bar counts its own
 * written beats (the grid ScoreView's playhead and the band's playhead use).
 * The identity unless a meter change alters the beat's note value.
 */
export function barGridBeat(
  timeline: Pick<Timeline, 'barStarts' | 'barScales'>,
  beat: number,
): number {
  const { barStarts, barScales = [] } = timeline;
  let drawnStart = 0;
  for (let i = 0; i < barStarts.length; i++) {
    const next = barStarts[i + 1];
    if (next === undefined || beat < next) {
      return drawnStart + (beat - barStarts[i]) / (barScales[i] || 1);
    }
    drawnStart += (next - barStarts[i]) / (barScales[i] || 1);
  }
  return beat;
}

/**
 * Lookahead selection: the events (from `fromIndex` onward) whose onset is at or
 * before `uptoBeat`, plus the next index to resume from. Events must be sorted
 * by absBeat (buildTimeline guarantees this). Lets the transport schedule only
 * the near-future window instead of queueing the whole piece at once.
 */
export function eventsDueBy(
  events: PlayEvent[],
  fromIndex: number,
  uptoBeat: number,
): { due: PlayEvent[]; nextIndex: number } {
  let i = Math.max(0, fromIndex);
  const due: PlayEvent[] = [];
  while (i < events.length && events[i].absBeat <= uptoBeat) {
    due.push(events[i]);
    i++;
  }
  return { due, nextIndex: i };
}

/** Global bar index and local beat for an absolute beat position, or null. */
export function locateBeat(barStarts: number[], barBeats: number[], absBeat: number): {
  barIndex: number;
  localBeat: number;
} | null {
  for (let i = 0; i < barStarts.length; i++) {
    if (absBeat >= barStarts[i] && absBeat < barStarts[i] + barBeats[i]) {
      return { barIndex: i, localBeat: absBeat - barStarts[i] };
    }
  }
  return null;
}
